/**
 * Storm — the battle-royale zone as a sequence of stages.
 *
 * Each stage: the zone HOLDS where it is for `holdMs` (the next zone is already
 * announced, so players can move), then SHRINKS for `shrinkMs` to a new square
 * of `size` px placed at random *inside* the current one — the next zone can be
 * anywhere within the current, so it is not always the centre. Damage per tick
 * is per stage and rises through the match.
 *
 * Pure: geometry, random placement (seeded, so a match can be replayed and a
 * test is deterministic), and shape-at-time. Shared so the client can draw the
 * moving boundary between the server's one-per-second updates by evaluating
 * exactly the same interpolation.
 */

/** Deterministic PRNG (mulberry32) — a match seed gives a reproducible storm. */
export function makeRng(seed) {
  let a = (seed >>> 0) || 0x9e3779b9;
  return function rng() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A square zone, centre + side. */
export function square(cx, cy, size) {
  return { cx, cy, size: Math.max(0, size) };
}

export function squareBounds(sq) {
  const h = sq.size / 2;
  return { minX: sq.cx - h, maxX: sq.cx + h, minY: sq.cy - h, maxY: sq.cy + h };
}

/**
 * Where the next zone goes: a `size` square placed uniformly at random so that
 * it lies entirely inside `current` (and inside the map). When the next size is
 * not smaller than the current, it simply shares the centre.
 * @param {{cx:number,cy:number,size:number}} current
 * @param {number} size
 * @param {() => number} rng
 * @param {{width:number,height:number}} map
 * @param {{ maxOffsetFraction?: number }} [opts] 0..1 — how far from the centre the new zone may be placed
 *        as a fraction of the maximum possible offset (1 = anywhere inside; 0 = always concentric)
 */
export function nextSquare(current, size, rng, map, opts = {}) {
  const s = Math.max(0, Math.min(size, current.size));
  const maxOffset = Math.max(0, (current.size - s) / 2) * (opts.maxOffsetFraction ?? 1);
  let cx = current.cx + (rng() * 2 - 1) * maxOffset;
  let cy = current.cy + (rng() * 2 - 1) * maxOffset;
  // Keep the whole zone on the map.
  const h = s / 2;
  if (map?.width) cx = Math.min(Math.max(cx, h), map.width - h);
  if (map?.height) cy = Math.min(Math.max(cy, h), map.height - h);
  return square(cx, cy, s);
}

/** Linear interpolation between two squares (centre and size). */
export function lerpSquare(a, b, t) {
  const k = Math.max(0, Math.min(1, t));
  return square(a.cx + (b.cx - a.cx) * k, a.cy + (b.cy - a.cy) * k, a.size + (b.size - a.size) * k);
}

/**
 * Linear interpolation between two rect bounds ({minX,maxX,minY,maxY}) — same clamp,
 * same linear blend as lerpSquare, just on bounds directly. The client (which only ever
 * sees bounds over the wire, never {cx,cy,size}) interpolates through this instead of
 * re-deriving the blend, so the wall it draws and the wall that damages players agree by
 * construction rather than by algebra (lerp commuting with the affine cx/cy/size → bounds
 * map) — see GameState.zoneView().
 */
export function lerpBounds(a, b, t) {
  const k = Math.max(0, Math.min(1, t));
  const lerp = (p, q) => p + (q - p) * k;
  return { minX: lerp(a.minX, b.minX), maxX: lerp(a.maxX, b.maxX), minY: lerp(a.minY, b.minY), maxY: lerp(a.maxY, b.maxY) };
}

/**
 * Normalize a stage list from config. Every field has a default so a stage may
 * be as short as `{ size: 1800 }`.
 */
export function normalizeStages(stages = []) {
  return stages.map((s, i) => ({
    index: i,
    holdMs: Math.max(0, Number(s.holdMs ?? 20000)),
    shrinkMs: Math.max(0, Number(s.shrinkMs ?? 15000)),
    size: Math.max(0, Number(s.size ?? s.radius ?? 0)),
    damagePerTick: Math.max(0, Number(s.damagePerTick ?? 0)),
    tickIntervalMs: Math.max(50, Number(s.tickIntervalMs ?? 1000))
  }));
}

/**
 * Convert the pre-5C `phases` config (each phase = continuous shrink to the next
 * phase's radius over durationMs) into stages with no hold, so old configs keep
 * working unchanged.
 *
 * The terminal phase's `durationMs`/`damagePerTick` are NOT carried over: a phase
 * only has meaning as "shrink toward the next phase's radius", and the last phase
 * has no next radius to shrink toward, so it converts to nothing rather than to a
 * guessed hold or shrink. Under the pre-5C engine that terminal phase was real (in
 * the old battle-royale config, a 15 s / 25 dmg-per-tick zero-radius window); a
 * config still using `phases` therefore ends one phase earlier and never applies
 * that final rate. No shipped config uses `phases` any more.
 */
export function stagesFromLegacyPhases(phases = []) {
  const out = [];
  for (let i = 0; i < phases.length - 1; i++) {
    const p = phases[i];
    const next = phases[i + 1];
    out.push({
      holdMs: 0,
      shrinkMs: Number(p.durationMs) || 0,
      size: Number(next.radius ?? next.size ?? 0),
      damagePerTick: Number(p.damagePerTick) || 0,
      tickIntervalMs: Number(p.tickIntervalMs) || 1000
    });
  }
  return normalizeStages(out);
}

/**
 * Build the whole storm up front: for each stage, the square it starts from and
 * the square it shrinks to. Deterministic given the seed.
 * @returns {Array<{index, holdMs, shrinkMs, damagePerTick, tickIntervalMs, from, to}>}
 */
export function planStorm(stages, initial, rng, map, opts = {}) {
  const plan = [];
  let current = initial;
  for (const stage of stages) {
    const to = nextSquare(current, stage.size, rng, map, opts);
    plan.push({ ...stage, from: current, to });
    current = to;
  }
  return plan;
}

/**
 * The zone at `elapsedMs` into a planned stage.
 * @returns {{ phase: 'hold'|'shrink'|'done', shape: {cx,cy,size}, t: number, phaseRemainingMs: number }}
 */
export function stageStateAt(stage, elapsedMs) {
  if (elapsedMs < stage.holdMs) {
    return { phase: 'hold', shape: stage.from, t: 0, phaseRemainingMs: stage.holdMs - elapsedMs };
  }
  const shrinkElapsed = elapsedMs - stage.holdMs;
  if (stage.shrinkMs > 0 && shrinkElapsed < stage.shrinkMs) {
    const t = shrinkElapsed / stage.shrinkMs;
    return { phase: 'shrink', shape: lerpSquare(stage.from, stage.to, t), t, phaseRemainingMs: stage.shrinkMs - shrinkElapsed };
  }
  return { phase: 'done', shape: stage.to, t: 1, phaseRemainingMs: 0 };
}

export const Storm = { makeRng, square, squareBounds, nextSquare, lerpSquare, lerpBounds, normalizeStages, stagesFromLegacyPhases, planStorm, stageStateAt };
